import { alpha, PaletteMode, Shadows, type ThemeOptions } from '@mui/material';
// noinspection ES6UnusedImports
import {} from '@mui/x-data-grid/themeAugmentation';
import { z } from 'zod';

// Semantic "active / in progress" blue. The whitelabel brand palette has no
// blue token, so this is a dedicated, theme-driven status color. Only `main` is
// provided (consumers read `.main`); it is NOT auto-augmented with contrastText.
declare module '@mui/material/styles' {
  interface Palette {
    inProgress: { main: string };
  }
  interface PaletteOptions {
    inProgress?: { main: string };
  }
}

// Defaults used both as the fallback-palette value and as the runtime default
// when a customer's serialized palette omits the (optional) inProgress token.
const DEFAULT_IN_PROGRESS_LIGHT = 'hsl(211, 90%, 45%)';
const DEFAULT_IN_PROGRESS_DARK = 'hsl(211, 90%, 62%)';

// Only links that leave the app (new tab or absolute URL) get the external
// link icon. In-app router links render relative hrefs and contact links
// (mailto:, tel:) open a client, so neither matches. Links whose content is
// not text (an image thumbnail, an icon-only card) opt out with
// `data-no-external-icon`.
const EXTERNAL_LINK_MATCHERS = [
  '[target="_blank"]',
  '[href^="http://"]',
  '[href^="https://"]',
  '[href^="//"]',
].map(
  (matcher) =>
    `${matcher}:not([href^="mailto:"]):not([href^="tel:"]):not([data-no-external-icon])`
);

const EXTERNAL_LINK_ICON_SELECTOR = EXTERNAL_LINK_MATCHERS.map(
  (matcher) => `&${matcher}::after`
).join(', ');

const EXTERNAL_LINK_ICON_HOVER_SELECTOR = EXTERNAL_LINK_MATCHERS.map(
  (matcher) => `&${matcher}:hover::after`
).join(', ');

// Same path as `@mui/icons-material/OpenInNew`, so text links match the
// icon buttons that render the component. Used as a mask so the glyph paints
// with the link's `currentColor` instead of a color baked into the SVG.
const OPEN_IN_NEW_ICON_PATH =
  'M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3z';

const OPEN_IN_NEW_ICON_MASK = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path d='${OPEN_IN_NEW_ICON_PATH}'/></svg>`
)}")`;

// Pure theme configuration function that can be used without store dependencies
export const createThemeSettings = (
  mode: PaletteMode,
  themePalettes: ThemePalettes
): ThemeOptions => {
  return {
    layout: {
      sidebar: {
        collapsed: 65,
        regular: 230,
      },
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const severity = ownerState.severity || 'info';
            const color =
              ownerState.variant === 'filled'
                ? theme.palette[severity].dark
                : theme.palette[severity].main;
            return {
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                width: 4,
                backgroundColor: color,
              },
            };
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          input: {
            '&': {
              minWidth: 'unset !important',
            },
          },
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          variant: 'h3',
        },
        styleOverrides: {
          root: {
            paddingRight: 48,
          },
        },
      },
      MuiDialogContent: {
        defaultProps: {
          dividers: true,
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            // Apply dynamic letter spacing for better readability
            // Based on Inter font recommendations for optimal character spacing
            variants: [
              {
                props: { variant: 'h1' },
                style: {
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                },
              },
              {
                props: { variant: 'h2' },
                style: {
                  letterSpacing: '-0.015em',
                  lineHeight: 1.25,
                },
              },
              {
                props: { variant: 'h3' },
                style: {
                  letterSpacing: '-0.01em',
                  lineHeight: 1.3,
                },
              },
              {
                props: { variant: 'h4' },
                style: {
                  letterSpacing: '-0.005em',
                  lineHeight: 1.35,
                },
              },
              {
                props: { variant: 'body1' },
                style: {
                  letterSpacing: '0.01em',
                  lineHeight: 1.5,
                },
              },
              {
                props: { variant: 'body2' },
                style: {
                  letterSpacing: '0.015em',
                  lineHeight: 1.45,
                },
              },
              {
                props: { variant: 'caption' },
                style: {
                  letterSpacing: '0.025em',
                  lineHeight: 1.4,
                },
              },
            ],
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: theme.transitions.create(
              ['transform', 'background-color', 'color', 'box-shadow'],
              {
                duration: theme.transitions.duration.shortest,
                easing: theme.transitions.easing.easeOut,
              }
            ),
            willChange: 'transform',
            '&:active': {
              transform: 'scale(0.96)',
            },
            '&.Mui-disabled': {
              transform: 'none',
            },
            '&:focus-visible, &.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          }),
          // Subtle, themed hover for solid buttons. MUI's default darkens by the
          // full tonal offset (~20%), which on the red primary reads as
          // near-black. A ~10% shift keeps the brand color recognizable.
          contained: ({ theme, ownerState }) => {
            const color = ownerState.color;
            const main =
              color && color !== 'inherit'
                ? theme.palette[color]?.main
                : undefined;
            const base = main ?? theme.palette.primary.main;
            return {
              '&:hover': {
                backgroundColor:
                  mode === 'dark'
                    ? `color-mix(in oklch, ${base}, #fff 14%)`
                    : `color-mix(in oklch, ${base}, #000 10%)`,
              },
            };
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            borderRadius: '50%',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          filled: ({ theme, ownerState }) => {
            const color = ownerState.color;
            if (!color || color === 'default') return {};
            const main = theme.palette[color]?.main;
            if (!main) return {};
            return {
              backgroundColor:
                mode === 'dark'
                  ? `color-mix(in oklch, ${main}, ${theme.palette.background.paper} 70%)`
                  : `color-mix(in oklch, ${main}, #fff 75%)`,
              color:
                mode === 'dark'
                  ? `color-mix(in oklch, ${main}, #fff 50%)`
                  : `color-mix(in oklch, ${main}, #000 55%)`,
            };
          },
        },
        variants: [
          {
            props: { color: 'success', variant: 'outlined' },
            style: ({ theme }) => ({
              backgroundColor: alpha(
                theme.palette.success.main,
                mode === 'dark' ? 0.2 : 0.1
              ),
            }),
          },
          {
            props: { color: 'error', variant: 'outlined' },
            style: ({ theme }) => ({
              backgroundColor: alpha(
                theme.palette.error.main,
                mode === 'dark' ? 0.2 : 0.1
              ),
            }),
          },
          {
            props: { color: 'info', variant: 'outlined' },
            style: ({ theme }) => ({
              backgroundColor: alpha(
                theme.palette.info.main,
                mode === 'dark' ? 0.2 : 0.1
              ),
            }),
          },
          {
            props: { color: 'warning', variant: 'outlined' },
            style: ({ theme }) => ({
              backgroundColor: alpha(
                theme.palette.warning.main,
                mode === 'dark' ? 0.2 : 0.1
              ),
            }),
          },
          {
            props: { color: 'primary', variant: 'outlined' },
            style: ({ theme }) => ({
              backgroundColor: alpha(
                theme.palette.primary.main,
                mode === 'dark' ? 0.2 : 0.1
              ),
            }),
          },
          {
            props: { color: 'secondary', variant: 'outlined' },
            style: ({ theme }) => ({
              backgroundColor: alpha(
                theme.palette.secondary.main,
                mode === 'dark' ? 0.2 : 0.1
              ),
            }),
          },
          {
            props: { color: 'default', variant: 'outlined' },
            style: ({ theme }) => ({
              backgroundColor: alpha(
                theme.palette.text.primary,
                mode === 'dark' ? 0.2 : 0.1
              ),
            }),
          },
        ],
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border:
              mode === 'dark'
                ? '0.5px solid hsl(0, 3%, 52%) !important'
                : '0.5px solid #e0e0e0',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme }) => ({
            [EXTERNAL_LINK_ICON_SELECTOR]: {
              // Browsers without CSS alt text drop the second declaration and
              // keep the first, so the icon still renders there.
              content: ['""', '"" / "(external link)"'],
              display: 'inline-block',
              flexShrink: 0,
              width: '1em',
              height: '1em',
              marginLeft: '0.125em',
              verticalAlign: '-0.125em',
              backgroundColor: 'currentColor',
              mask: `${OPEN_IN_NEW_ICON_MASK} center / contain no-repeat`,
              transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.shortest,
              }),
            },
            '&:hover': {
              textDecoration: 'underline',
              textDecorationColor: theme.palette.primary.main,
              textDecorationThickness: '1.25px',
              textUnderlineOffset: '3px',
            },
            [EXTERNAL_LINK_ICON_HOVER_SELECTOR]: {
              transform: 'scale(1.07) translateY(-1px)',
            },
          }),
        },
      },
      MuiMenu: {
        defaultProps: {
          transitionDuration: 120,
        },
        styleOverrides: {
          paper: {
            '@keyframes menuEnter': {
              from: {
                transform: 'scale(0.9) translateY(-4px)',
              },
              to: {
                transform: 'scale(1) translateY(0)',
              },
            },
            animation: 'menuEnter 120ms cubic-bezier(0.32, 0.72, 0, 1)',
          },
        },
      },
      MuiUseMediaQuery: {
        defaultProps: {
          noSsr: true,
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderBottomWidth: 0.5,
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          elevation: 1,
          transitionDuration: 225,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        },
        styleOverrides: {
          paper: {
            transform: 'translateY(4px) !important',
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            wordBreak: 'break-word',
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: theme.transitions.create(
              ['border-color', 'box-shadow'],
              {
                duration: theme.transitions.duration.shortest,
                easing: theme.transitions.easing.easeInOut,
              }
            ),
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }),
          notchedOutline: ({ theme }) => ({
            transition: theme.transitions.create(['border-color'], {
              duration: theme.transitions.duration.shortest,
              easing: theme.transitions.easing.easeInOut,
            }),
          }),
        },
      },
      MuiPopper: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiDataGrid-filterForm': {
              gap: '8px',
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                alignItems: 'stretch',
                padding: theme.spacing(1, 0.5),
              },
              '& .MuiFormControl-root': {
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                },
              },
              // Stack order on mobile: fields first, the delete (trash) button
              // last and right-aligned. Without an explicit order the delete
              // icon is the form's first child, so it lands top-right detached.
              '& .MuiDataGrid-filterFormColumnInput': {
                [theme.breakpoints.down('sm')]: { order: 1 },
              },
              '& .MuiDataGrid-filterFormOperatorInput': {
                [theme.breakpoints.down('sm')]: { order: 2 },
              },
              '& .MuiDataGrid-filterFormValueInput': {
                [theme.breakpoints.down('sm')]: { order: 3 },
              },
              '& .MuiDataGrid-filterFormDeleteIcon': {
                [theme.breakpoints.down('sm')]: {
                  order: 4,
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  display: 'flex',
                  '& .MuiButtonBase-root': {
                    marginLeft: 'auto',
                  },
                },
              },
            },
          }),
        },
      },
      MuiDataGrid: {
        defaultProps: {
          slotProps: {
            panel: {
              sx: {
                '& .MuiDataGrid-panelWrapper': {
                  maxWidth: 'calc(100vw - 4rem)',
                },
                '& .MuiDataGrid-filterFormValueInput': {
                  maxWidth: '500px',
                  width: 'auto',
                },
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: ({ theme }) => ({
            width: 34,
            height: 19,
            marginRight: 5,
            padding: 0,
            display: 'flex',
            borderRadius: 19 / 2,
            transition: theme.transitions.create(['box-shadow'], {
              duration: 200,
            }),
            '&:hover:not(:has(.Mui-disabled)), .MuiFormControlLabel-root:hover > &:not(:has(.Mui-disabled))':
              {
                boxShadow: theme.shadows[4],
              },
            '&:has(.Mui-focusVisible)': {
              borderRadius: 19 / 2,
              boxShadow: `
                0 0 0 2px ${theme.palette.background.paper},
                0 0 0 4px ${theme.palette.primary.main},
                0 2px 8px 0 rgba(0, 0, 0, 0.15)
              `,
            },
            '&:active:not(.Mui-disabled)': {
              '& .MuiSwitch-thumb': {
                width: 18,
              },
              '& .MuiSwitch-switchBase.Mui-checked': {
                transform: 'translateX(11px)',
              },
            },
            // Disabled state styling
            '&.Mui-disabled': {
              opacity: theme.palette.action.disabledOpacity,
              cursor: 'not-allowed',
              '& .MuiSwitch-track': {
                backgroundColor: theme.palette.action.disabledBackground,
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: theme.palette.background.paper,
                boxShadow: `0 1px 2px 0 ${theme.palette.action.disabled}`,
              },
              '& .MuiSwitch-switchBase.Mui-checked': {
                '& .MuiSwitch-thumb': {
                  backgroundColor: theme.palette.background.paper,
                },
                '& + .MuiSwitch-track': {
                  backgroundColor: theme.palette.action.disabledBackground,
                },
              },
            },
            '& .MuiSwitch-switchBase': {
              padding: 2,
              '&.Mui-checked': {
                transform: 'translateX(15px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                  opacity: 1,
                },
              },
              '&.Mui-disabled': {
                color: theme.palette.action.disabled,
              },
            },
            '& .MuiSwitch-thumb': {
              boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
              width: 14,
              height: 14,
              borderRadius: 7,
              transition: theme.transitions.create(['width'], {
                duration: 200,
              }),
            },
            '& .MuiSwitch-track': {
              borderRadius: 19 / 2,
              opacity: 1,
              backgroundColor: 'rgba(0,0,0,.25)',
              boxSizing: 'border-box',
              ...theme.applyStyles('dark', {
                backgroundColor: 'rgba(255,255,255,.35)',
              }),
            },
          }),
        },
      },
    },
    shape: {
      borderRadius: 7,
    },
    palette: {
      mode: mode,
      ...(mode === 'dark' ? themePalettes.dark : themePalettes.light),
      // Guarantee the inProgress token exists even when a customer's serialized
      // palette omits the optional field — otherwise theme.palette.inProgress
      // would be undefined and crash consumers.
      inProgress: (mode === 'dark' ? themePalettes.dark : themePalettes.light)
        .inProgress ?? {
        main:
          mode === 'dark'
            ? DEFAULT_IN_PROGRESS_DARK
            : DEFAULT_IN_PROGRESS_LIGHT,
      },
    },
    typography: {
      fontFamily: ['Figtree', 'sans-serif'].join(','),
      fontSize: 14,
      h1: {
        fontSize: 'var(--fs-h1)',
      },
      h2: {
        fontSize: 'var(--fs-h2)',
      },
      h3: {
        fontSize: 'var(--fs-h3)',
      },
      h4: {
        fontSize: 'var(--fs-h4)',
      },
      h5: {
        fontSize: 'var(--fs-h5)',
      },
      h6: {
        fontSize: 'var(--fs-h6)',
      },
      subtitle1: {
        fontSize: 'var(--fs-subtitle1)',
      },
      subtitle2: {
        fontSize: 'var(--fs-subtitle2)',
      },
      body1: {
        fontSize: 'var(--fs-body1)',
      },
      body2: {
        fontSize: 'var(--fs-body2)',
      },
      button: {
        fontSize: 'var(--fs-button)',
      },
      caption: {
        fontSize: 'var(--fs-caption)',
      },
      overline: {
        fontSize: 'var(--fs-overline)',
      },
    },
    transitions: {
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
    shadows: (() => {
      // Joshwcomeau layered-shadow approach: single light source from top,
      // multiple stacked layers with same hue but increasing blur/y-offset,
      // hue tinted (not pure black) so it blends with paper.
      const shadowColor = mode === 'dark' ? '220deg 50% 2%' : '220deg 25% 18%';
      const lowO = mode === 'dark' ? 0.45 : 0.18;
      const midO = mode === 'dark' ? 0.5 : 0.22;
      const highO = mode === 'dark' ? 0.6 : 0.28;

      // Resting (border-tier) — even cards get a perceptible lift.
      const border = [
        `0 0.5px 1px hsl(${shadowColor} / ${lowO})`,
        `0 1px 2px hsl(${shadowColor} / ${lowO * 0.7})`,
      ].join(', ');

      const low = [
        `0 1px 1px hsl(${shadowColor} / ${lowO})`,
        `0 2px 4px -1px hsl(${shadowColor} / ${lowO * 0.85})`,
        `0 4px 8px -2px hsl(${shadowColor} / ${lowO * 0.7})`,
      ].join(', ');

      const medium = [
        `0 1px 1px hsl(${shadowColor} / ${midO})`,
        `0 3px 5px -1px hsl(${shadowColor} / ${midO * 0.9})`,
        `0 8px 12px -2px hsl(${shadowColor} / ${midO * 0.8})`,
        `0 16px 24px -4px hsl(${shadowColor} / ${midO * 0.7})`,
      ].join(', ');

      const high = [
        `0 0 0 1px hsl(${shadowColor} / ${highO * 0.35})`,
        `0 2px 4px hsl(${shadowColor} / ${highO * 0.6})`,
        `0 6px 10px -2px hsl(${shadowColor} / ${highO * 0.7})`,
        `0 14px 22px -4px hsl(${shadowColor} / ${highO * 0.8})`,
        `0 28px 44px -8px hsl(${shadowColor} / ${highO * 0.9})`,
        `0 48px 72px -16px hsl(${shadowColor} / ${highO})`,
      ].join(', ');

      return [
        'none', // 0
        border, // 1 — border-like
        low, // 2 — low elevation
        low, // 3
        medium, // 4 — medium elevation
        medium, // 5
        high, // 6 — high elevation
        high, // 7
        high, // 8
        high, // 9
        high, // 10
        high, // 11
        high, // 12
        high, // 13
        high, // 14
        high, // 15
        high, // 16
        high, // 17
        high, // 18
        high, // 19
        high, // 20
        high, // 21
        high, // 22
        high, // 23
        high, // 24
      ] as Shadows;
    })(),
  };
};

export const themePaletteSchema = z.object({
  contrastThreshold: z.number().int().positive().optional(),
  primary: z.object({
    main: z.string().trim().min(1),
  }),
  background: z.object({
    default: z.string().trim().min(1),
    paper: z.string().trim().min(1),
    darker: z.string().trim().min(1),
  }),
  secondary: z.object({
    main: z.string().trim().min(1),
  }),
  action: z.object({
    hover: z.string().trim().min(1),
    active: z.string().trim().min(1),
    disabled: z.string().trim().min(1),
    focus: z.string().trim().min(1),
  }),
  text: z.object({
    primary: z.string().trim().min(1),
  }),
  error: z.object({
    main: z.string().trim().min(1),
  }),
  warning: z.object({
    main: z.string().trim().min(1),
  }),
  success: z.object({
    main: z.string().trim().min(1),
  }),
  info: z.object({
    main: z.string().trim().min(1),
  }),
  // Optional so existing customer palette configs keep validating; defaulted at
  // theme-build time when absent (see createThemeSettings).
  inProgress: z
    .object({
      main: z.string().trim().min(1),
    })
    .optional(),
});

export type ThemePaletteSerialized = z.infer<typeof themePaletteSchema>;

export type ThemePalettes = {
  dark: ThemePaletteSerialized;
  light: ThemePaletteSerialized;
};

export const FALLBACK_PARKDNA_DEFAULT_LIGHT_THEME_PALETTE: ThemePaletteSerialized =
  {
    contrastThreshold: 3,
    primary: {
      main: '#E60017',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
      darker: '#F8F9FA',
    },
    secondary: {
      main: 'hsl(48, 95%, 76%)',
    },
    action: {
      hover: 'rgba(204, 0, 34, 0.2)', // Pre-computed alpha result
      active: '#E60017',
      disabled: 'hsl(41, 9%, 35%)',
      focus: '#B30019',
    },
    text: {
      primary: '#2D2D2D',
    },
    error: {
      main: 'hsl(360, 67%, 44%)',
    },
    warning: {
      main: 'hsl(45,99%,48%)',
    },
    success: {
      main: '#45c17a',
    },
    info: {
      main: 'hsl(40, 13%, 23%)',
    },
    inProgress: {
      main: DEFAULT_IN_PROGRESS_LIGHT,
    },
  };

export const FALLBACK_DEFAULT_PARKDNA_DARK_THEME_PALETTE: ThemePaletteSerialized =
  {
    contrastThreshold: 3,
    primary: {
      main: '#FF334D',
    },
    background: {
      default: 'hsl(40, 11%, 15%)',
      paper: 'hsl(40, 11%, 12%)',
      darker: '#1E1E1E',
    },
    secondary: {
      main: 'hsl(50,92%,67%)',
    },
    action: {
      hover: 'rgba(255, 85, 102, 0.2)', // Pre-computed alpha result
      active: '#FF334D',
      // Must stay clearly lighter than background.default (L 15%): MUI uses
      // this for disabled input borders, text, and icons. Matches the blended
      // result of MUI's dark default rgba(255, 255, 255, 0.3).
      disabled: 'hsl(40, 8%, 40%)',
      focus: '#FF1A33',
    },
    text: {
      primary: '#FFFFFF',
    },
    error: {
      main: 'hsl(360, 67%, 50%)',
    },
    warning: {
      main: 'hsl(44,98%,48%)',
    },
    success: {
      main: 'hsl(146, 50%, 40%)',
    },
    info: {
      main: 'hsl(40, 13%, 50%)',
    },
    inProgress: {
      main: DEFAULT_IN_PROGRESS_DARK,
    },
  };
